'use client';

import { useCallback, useRef, useEffect } from 'react';
import { useRouter } from 'next/navigation';

interface PrefetchOptions {
  debounceMs?: number;
  maxConcurrent?: number;
  ttlMs?: number;
}

interface PrefetchReturn {
  prefetch: (path: string) => void;
  prefetchOnHover: (path: string) => void;
  cancelHoverPrefetch: (path: string) => void;
  clearCache: () => void;
}

// Cache entry type
interface CacheEntry {
  timestamp: number;
  promise: Promise<void>;
}

/**
 * Smart prefetch hook with:
 * - Debouncing for hover (200ms default)
 * - Max concurrent prefetches (3 default)
 * - TTL for cache entries (5 minutes default)
 * - Automatic cleanup
 */
export function usePrefetch(options: PrefetchOptions = {}): PrefetchReturn {
  const router = useRouter();
  
  const {
    debounceMs = 200,
    maxConcurrent = 3,
    ttlMs = 5 * 60 * 1000, // 5 minutes
  } = options;
  
  // Track prefetch cache with TTL
  const prefetchCache = useRef<Map<string, CacheEntry>>(new Map());
  const activePrefetches = useRef<Set<string>>(new Set());
  const hoverTimeouts = useRef<Map<string, NodeJS.Timeout>>(new Map());
  
  // Clean expired cache entries
  const cleanCache = useCallback(() => {
    const now = Date.now();
    for (const [path, entry] of prefetchCache.current.entries()) {
      if (now - entry.timestamp > ttlMs) {
        prefetchCache.current.delete(path);
      }
    }
  }, [ttlMs]);
  
  // Core prefetch function
  const prefetch = useCallback((path: string) => {
    // Check cache first
    const cached = prefetchCache.current.get(path);
    if (cached && Date.now() - cached.timestamp < ttlMs) {
      return; // Still valid in cache
    }
    
    // Check concurrent limit
    if (activePrefetches.current.size >= maxConcurrent) {
      return; // Too many active prefetches
    }
    
    // Mark as active
    activePrefetches.current.add(path);
    
    // Create prefetch promise
    const promise = Promise.resolve(router.prefetch(path)).finally(() => {
      activePrefetches.current.delete(path);
    });
    
    // Cache it
    prefetchCache.current.set(path, {
      timestamp: Date.now(),
      promise,
    });
    
    return promise;
  }, [router, ttlMs, maxConcurrent]);
  
  // Hover prefetch with debounce
  const prefetchOnHover = useCallback((path: string) => {
    // Clear any existing timeout for this path
    const existingTimeout = hoverTimeouts.current.get(path);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }
    
    // Set new debounced timeout
    const timeoutId = setTimeout(() => {
      prefetch(path);
      hoverTimeouts.current.delete(path);
    }, debounceMs);
    
    hoverTimeouts.current.set(path, timeoutId);
  }, [prefetch, debounceMs]);
  
  // Cancel hover prefetch
  const cancelHoverPrefetch = useCallback((path: string) => {
    const timeout = hoverTimeouts.current.get(path);
    if (timeout) {
      clearTimeout(timeout);
      hoverTimeouts.current.delete(path);
    }
  }, []);
  
  // Clear all caches
  const clearCache = useCallback(() => {
    // Clear timeouts
    for (const timeout of hoverTimeouts.current.values()) {
      clearTimeout(timeout);
    }
    hoverTimeouts.current.clear();
    
    // Clear caches
    prefetchCache.current.clear();
    activePrefetches.current.clear();
  }, []);
  
  // Periodic cache cleanup
  useEffect(() => {
    const interval = setInterval(cleanCache, 60000); // Clean every minute
    return () => {
      clearInterval(interval);
      clearCache();
    };
  }, [cleanCache, clearCache]);
  
  return {
    prefetch,
    prefetchOnHover,
    cancelHoverPrefetch,
    clearCache,
  };
}